import { createRouter, createWebHistory } from "vue-router";
import { Modal } from "bootstrap";
import store from "../store";
import { RUTAS_PERMISOS } from "@/config/roles";
import { isRouteOffline } from "@/config/offlineConfig";
import {
  destinoSegunHabilitacion,
  modoPermiteCriaPropia,
} from "@/utils/cria/criaRutasHelper";
import {
  TAMBO_LITE_ALLOWED_ROUTE_NAMES,
  TAMBO_LITE_MODE,
} from "@/config/liteBeta";

const TAMBO_LITE_ROUTE_SET = new Set(TAMBO_LITE_ALLOWED_ROUTE_NAMES);

// ============================================================
// LAZY LOADING: Todas las vistas se cargan bajo demanda.
// Solo LoginView se importa estáticamente por ser la landing page.
// ============================================================
const LoginView = () =>
  import(/* webpackChunkName: "auth" */ "@/views/LoginView.vue");
const RegisterView = () =>
  import(/* webpackChunkName: "auth" */ "@/views/RegisterView.vue");
const ForgotPasswordView = () =>
  import(/* webpackChunkName: "auth" */ "@/views/ForgotPasswordView.vue");
const LandingPageView = () =>
  import(/* webpackChunkName: "public" */ "@/views/LandingPageView.vue");
const TfpDevView = () =>
  import(/* webpackChunkName: "public" */ "@/views/TfpDevView.vue");
const TfpAgroView = () =>
  import(/* webpackChunkName: "public" */ "@/views/TfpAgroView.vue");
const SuscripcionesView = () =>
  import(/* webpackChunkName: "public" */ "@/views/SuscripcionesView.vue");
const TermsView = () =>
  import(/* webpackChunkName: "public" */ "@/views/TermsView.vue");
const VerifyView = () =>
  import(/* webpackChunkName: "auth" */ "@/views/VerifyView.vue");
const TambosView = () =>
  import(/* webpackChunkName: "core" */ "@/views/TambosView.vue");
const InicioView = () =>
  import(/* webpackChunkName: "core" */ "@/views/InicioView.vue");
const DespachosView = () =>
  import(/* webpackChunkName: "produccion" */ "@/views/DespachosView.vue");
const EmpresasView = () =>
  import(/* webpackChunkName: "produccion" */ "@/views/EmpresasView.vue");
const VacasView = () =>
  import(/* webpackChunkName: "rodeo" */ "@/views/VacasView.vue");
const RodeoCriaView = () =>
  import(/* webpackChunkName: "rodeo" */ "@/views/RodeoCriaView.vue");
// Eficiencia reproductiva de cría (solo_cria / ciclo_completo)
const CriaResumenView = () =>
  import(
    /* webpackChunkName: "cria-eficiencia" */ "@/views/cria/CriaResumenView.vue"
  );
const CriaCampaniaView = () =>
  import(
    /* webpackChunkName: "cria-eficiencia" */ "@/views/cria/CriaCampaniaView.vue"
  );
const CriaFichaEficienciaView = () =>
  import(
    /* webpackChunkName: "cria-eficiencia" */ "@/views/cria/CriaFichaEficienciaView.vue"
  );
const CriaRankingsView = () =>
  import(
    /* webpackChunkName: "cria-eficiencia" */ "@/views/cria/CriaRankingsView.vue"
  );
const CriaComparacionView = () =>
  import(
    /* webpackChunkName: "cria-eficiencia" */ "@/views/cria/CriaComparacionView.vue"
  );
const CriaCalidadView = () =>
  import(
    /* webpackChunkName: "cria-eficiencia" */ "@/views/cria/CriaCalidadView.vue"
  );
const LotesView = () =>
  import(/* webpackChunkName: "rodeo" */ "@/views/LotesView.vue");
const DietasView = () =>
  import(/* webpackChunkName: "alimentacion" */ "@/views/DietasView.vue");
const SiembrasView = () =>
  import(/* webpackChunkName: "agricultura" */ "@/views/SiembrasView.vue");
const CosechasView = () =>
  import(/* webpackChunkName: "agricultura" */ "@/views/CosechasView.vue");
const PicadosView = () =>
  import(/* webpackChunkName: "agricultura" */ "@/views/PicadosView.vue");
const PatioComidasView = () =>
  import(/* webpackChunkName: "alimentacion" */ "@/views/PatioComidasView.vue");
const VetInventarioView = () =>
  import(/* webpackChunkName: "rodeo" */ "@/views/VetInventarioView.vue");
const EstadisticasView = () =>
  import(/* webpackChunkName: "gestion" */ "@/views/EstadisticasView.vue");
const MaquinariasView = () =>
  import(/* webpackChunkName: "operaciones" */ "@/views/MaquinariasView.vue");
const OrdenesCompraView = () =>
  import(/* webpackChunkName: "operaciones" */ "@/views/OrdenesCompraView.vue");
const ParticipantesView = () =>
  import(/* webpackChunkName: "gestion" */ "@/views/ParticipantesView.vue");
const TareasView = () =>
  import(/* webpackChunkName: "operaciones" */ "@/views/TareasView.vue");
const ForrajesView = () =>
  import(/* webpackChunkName: "operaciones" */ "@/views/ForrajesView.vue");
const InsumosView = () =>
  import(/* webpackChunkName: "agricultura" */ "@/views/InsumosView.vue");
const VentasView = () =>
  import(/* webpackChunkName: "comercial" */ "@/views/VentasView.vue");
const FacturacionView = () =>
  import(/* webpackChunkName: "comercial" */ "@/views/FacturacionView.vue");
const ChequesView = () =>
  import(/* webpackChunkName: "comercial" */ "@/views/ChequesView.vue");
const GastosTercerizadosView = () =>
  import(
    /* webpackChunkName: "operaciones" */ "@/views/GastosTercerizadosView.vue"
  );
const CorralesView = () =>
  import(/* webpackChunkName: "feedlot" */ "@/views/CorralesView.vue");
const EngordeView = () =>
  import(/* webpackChunkName: "feedlot" */ "@/views/EngordeView.vue");
const CompraAnimalesView = () =>
  import(/* webpackChunkName: "feedlot" */ "@/views/CompraAnimalesView.vue");
const MovimientosRemotosView = () =>
  import(
    /* webpackChunkName: "gestion" */ "@/views/MovimientosRemotosView.vue"
  );
const ERPView = () =>
  import(/* webpackChunkName: "gestion" */ "@/views/ERPView.vue");
const ComprobantesERPView = () =>
  import(/* webpackChunkName: "gestion" */ "@/views/ComprobantesView.vue");
const PasturasView = () =>
  import(/* webpackChunkName: "alimentacion" */ "@/views/PasturasView.vue");
const MapaAgrView = () =>
  import(/* webpackChunkName: "agricultura" */ "@/views/MapaAgrView.vue");
const LibroIVAView = () =>
  import(/* webpackChunkName: "fiscal" */ "@/views/fiscal/LibroIVAView.vue");
const RetencionesView = () =>
  import(/* webpackChunkName: "fiscal" */ "@/views/fiscal/RetencionesView.vue");
const VentaAgroView = () =>
  import(/* webpackChunkName: "agricultura" */ "@/views/VentaAgroView.vue");
const TramitesSenasaView = () =>
  import(/* webpackChunkName: "fiscal" */ "@/views/TramitesSenasaView.vue");

// ============================================================
// Rutas que requieren modo específico:
//   requiresModo: 'tambo'    → solo tambo o mixto
//   requiresModo: 'feedlot'  → solo feedlot o mixto
// ============================================================

const routes = [
  {
    path: "/landing",
    name: "landing",
    component: LandingPageView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/vaquiano",
    name: "vaquiano",
    component: LandingPageView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/baquiano",
    name: "baquiano",
    component: LandingPageView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/tfp",
    name: "tfp",
    component: TfpDevView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/tfp-dev",
    name: "tfpDev",
    component: TfpDevView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/tfp-agro",
    name: "tfpAgro",
    component: TfpAgroView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/terminos",
    name: "terms",
    component: TermsView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/suscripciones",
    name: "suscripciones",
    component: SuscripcionesView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/register",
    name: "register",
    component: RegisterView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/forgot-password",
    name: "forgotPassword",
    component: ForgotPasswordView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/invitacion/:token",
    name: "invitacion",
    component: () => import("@/views/InvitacionView.vue"),
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/verify",
    name: "verify",
    component: VerifyView,
    meta: { requiresAuth: false, requiresTambo: false },
  },
  {
    path: "/",
    alias: "/tambos",
    name: "tambos",
    component: TambosView,
    meta: { requiresAuth: true, requiresTambo: false },
  },
  {
    path: "/inicio",
    name: "inicio",
    component: InicioView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/participantes",
    name: "participantes",
    component: ParticipantesView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/despachos",
    name: "despachos",
    component: DespachosView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "lecheria" },
  },
  {
    path: "/empresas",
    name: "empresas",
    component: EmpresasView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "lecheria" },
  },
  {
    path: "/vacas",
    name: "vacas",
    component: VacasView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "lecheria" },
  },
  {
    path: "/rodeo-cria",
    name: "cria",
    component: RodeoCriaView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "cria" },
  },
  {
    path: "/cria/eficiencia",
    name: "criaEficiencia",
    component: CriaResumenView,
    meta: {
      requiresAuth: true,
      requiresTambo: true,
      requiresModo: "cria_propia",
      requiresCriaEficiencia: true,
    },
  },
  {
    path: "/cria/campanias/:campaniaId",
    name: "criaCampania",
    component: CriaCampaniaView,
    meta: {
      requiresAuth: true,
      requiresTambo: true,
      requiresModo: "cria_propia",
      requiresCriaEficiencia: true,
    },
  },
  {
    path: "/cria/animales/:animalId/eficiencia",
    name: "criaFichaEficiencia",
    component: CriaFichaEficienciaView,
    meta: {
      requiresAuth: true,
      requiresTambo: true,
      requiresModo: "cria_propia",
      requiresCriaEficiencia: true,
    },
  },
  {
    path: "/cria/rankings",
    name: "criaRankings",
    component: CriaRankingsView,
    meta: {
      requiresAuth: true,
      requiresTambo: true,
      requiresModo: "cria_propia",
      requiresCriaEficiencia: true,
    },
  },
  {
    path: "/cria/comparacion",
    name: "criaComparacion",
    component: CriaComparacionView,
    meta: {
      requiresAuth: true,
      requiresTambo: true,
      requiresModo: "cria_propia",
      requiresCriaEficiencia: true,
    },
  },
  {
    path: "/cria/calidad",
    name: "criaCalidad",
    component: CriaCalidadView,
    meta: {
      requiresAuth: true,
      requiresTambo: true,
      requiresModo: "cria_propia",
      requiresCriaEficiencia: true,
    },
  },
  {
    path: "/lotes",
    name: "lotes",
    component: LotesView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "cria" },
  },
  {
    path: "/dietas",
    name: "dietas",
    component: DietasView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "animales" },
  },
  {
    path: "/maquinarias",
    name: "maquinarias",
    component: MaquinariasView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/siembras",
    name: "siembras",
    component: SiembrasView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/forrajes",
    name: "forrajes",
    component: ForrajesView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/insumos",
    name: "insumos",
    component: InsumosView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/cosechas",
    name: "cosechas",
    component: CosechasView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/pasturas",
    name: "pasturas",
    component: PasturasView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/mapa-agronomico",
    name: "mapaAgronomico",
    component: MapaAgrView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/picados",
    name: "picados",
    component: PicadosView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/patio-comidas",
    name: "patioComidas",
    component: PatioComidasView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "animales" },
  },
  {
    path: "/venta-agro",
    name: "ventaAgro",
    component: VentaAgroView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/veterinario",
    name: "veterinario",
    component: VetInventarioView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "animales" },
  },
  {
    path: "/estadisticas",
    name: "estadisticas",
    component: EstadisticasView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/ordenes-compra",
    name: "ordenesCompra",
    component: OrdenesCompraView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/tareas",
    name: "tareas",
    component: TareasView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/ventas",
    name: "ventas",
    component: VentasView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "animales" },
  },
  {
    path: "/facturacion",
    name: "facturacion",
    component: FacturacionView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/cheques",
    name: "cheques",
    component: ChequesView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/gastos-tercerizados",
    name: "gastosTercerizados",
    component: GastosTercerizadosView,
    meta: { requiresAuth: true, requiresTambo: true },
  },
  {
    path: "/sin-acceso",
    name: "sinAcceso",
    component: () => import("@/views/SinAccesoView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/corrales",
    name: "corralesFeedlot",
    component: CorralesView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "feedlot" },
  },
  {
    path: "/engorde",
    name: "engorde",
    component: EngordeView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "feedlot" },
  },
  {
    path: "/compra-animales",
    name: "compraAnimales",
    component: CompraAnimalesView,
    meta: { requiresAuth: true, requiresTambo: true, requiresModo: "animales" },
  },
  {
    path: "/auditoria",
    name: "auditoria",
    component: MovimientosRemotosView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/erp",
    name: "erp",
    component: ERPView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/comprobantes-erp",
    name: "comprobantesERP",
    component: ComprobantesERPView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/libro-iva",
    name: "libroIva",
    component: LibroIVAView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/fiscal/libro-iva",
    redirect: { name: "libroIva" },
  },
  {
    path: "/retenciones",
    name: "retenciones",
    component: RetencionesView,
    meta: { requiresAuth: true, requiresTambo: true, soloGestor: true },
  },
  {
    path: "/tramites-senasa",
    name: "tramitesSenasa",
    component: TramitesSenasaView,
    meta: {
      requiresAuth: true,
      requiresTambo: true,
      soloGestor: true,
      requiresModo: "animales",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0, left: 0 };
  },
});

// ============================================
// GUARD DE NAVEGACIÓN CON PERMISOS
// ============================================
router.beforeEach(async (to, from, next) => {
  if (TAMBO_LITE_MODE && !TAMBO_LITE_ROUTE_SET.has(to.name)) {
    return next({ name: "sinAcceso", query: { reason: "lite-beta" } });
  }

  const isAuthenticated =
    store.getters["userTamboStore/isAuthenticated"] ||
    !!localStorage.getItem("access_token");

  const hasTambo =
    store.getters["userTamboStore/hasSelectedTambo"] ||
    !!localStorage.getItem("current_tambo_id");

  const isGestor = store.getters["userTamboStore/isGestor"];
  const isPendiente = store.getters["userTamboStore/isPendiente"];

  // 0. Rutas públicas: salida temprana antes de cualquier otra comprobación
  if (to.meta.requiresAuth === false) {
    // Usuario ya autenticado que intenta ir a login/register/forgotPassword → redirigir a inicio
    if (
      (to.name === "login" ||
        to.name === "register" ||
        to.name === "forgotPassword") &&
      isAuthenticated
    ) {
      return next({ name: "tambos" });
    }
    return next();
  }

  // 1. (legacy, ya cubierto arriba, mantenido por seguridad)
  // ya autenticado → ir a inicio
  if (
    (to.name === "login" ||
      to.name === "register" ||
      to.name === "forgotPassword") &&
    isAuthenticated
  ) {
    return next({ name: "tambos" });
  }

  // 2. Requiere autenticación
  if (to.meta.requiresAuth !== false && !isAuthenticated) {
    return next({ name: "login" });
  }

  // 3. Requiere tambo seleccionado
  if (to.meta.requiresTambo && !hasTambo) {
    return next({ name: "tambos" });
  }

  // 4. Rol pendiente: solo puede ir a tambos / login / register / sinAcceso
  if (
    isPendiente &&
    !["tambos", "login", "register", "forgotPassword", "sinAcceso"].includes(
      to.name
    )
  ) {
    return next({ name: "tambos" });
  }

  // 4.5 Offline: solo permitir rutas habilitadas offline
  if (!navigator.onLine && !isRouteOffline(to.name)) {
    return next({ name: "sinAcceso", query: { reason: "offline" } });
  }

  // 5. Ruta solo gestores
  if (to.meta.soloGestor && !isGestor) {
    return next({ name: "sinAcceso" });
  }

  // 6. Modo de producción requerido
  if (to.meta.requiresModo && hasTambo) {
    const modo =
      store.getters["userTamboStore/currentModoProduccion"] || "tambo";
    const req = to.meta.requiresModo;
    const ok =
      (req === "tambo" &&
        ["tambo", "tambo_feedlot", "solo_cria", "ciclo_completo"].includes(
          modo
        )) ||
      (req === "lecheria" && ["tambo", "tambo_feedlot"].includes(modo)) ||
      (req === "feedlot" &&
        ["invernada", "tambo_feedlot", "ciclo_completo"].includes(modo)) ||
      (req === "cria" &&
        ["tambo", "tambo_feedlot", "solo_cria", "ciclo_completo"].includes(
          modo
        )) ||
      (req === "cria_propia" && modoPermiteCriaPropia(modo)) ||
      (req === "animales" &&
        [
          "tambo",
          "tambo_feedlot",
          "solo_cria",
          "invernada",
          "ciclo_completo",
        ].includes(modo));
    if (!ok) return next({ name: "sinAcceso" });
  }

  // 6.5 Vistas personalizadas del usuario (si están configuradas)
  const currentPermisos = store.getters["userTamboStore/currentPermisos"] || {};
  if (
    currentPermisos.vistas &&
    currentPermisos.vistas[to.name] !== undefined &&
    !isGestor
  ) {
    if (currentPermisos.vistas[to.name] === false) {
      return next({ name: "sinAcceso" });
    }
  }

  // 7. Permisos específicos de la ruta
  const permisosRequeridos = RUTAS_PERMISOS[to.name] || [];
  if (permisosRequeridos.length > 0 && hasTambo) {
    // Si la vista está explícitamente habilitada por configuración personalizada, se permite acceso
    if (currentPermisos.vistas && currentPermisos.vistas[to.name] === true) {
      // permitido
    } else {
      const hasPermission = store.getters["userTamboStore/hasPermission"];
      const tieneAcceso = permisosRequeridos.some((p) => hasPermission(p));
      if (!tieneAcceso) return next({ name: "sinAcceso" });
    }
  }

  // Limpiar modales de Bootstrap al navegar
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  document.querySelectorAll(".modal.show").forEach((modal) => {
    Modal.getInstance(modal)?.hide();
  });
  document.body.classList.remove("modal-open");
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";

  // 8. Eficiencia reproductiva de cría: oculta si el establecimiento no está habilitado
  // (404 funcion_no_habilitada) o el usuario no tiene permiso (403). FRONTEND_INTEGRATION §3.
  if (to.meta.requiresCriaEficiencia && hasTambo) {
    const habilitacion = await store.dispatch(
      "criaEficienciaStore/verificarHabilitacion"
    );
    // Un error pasajero (5xx o red) no oculta la sección: la pantalla muestra
    // "No se pudo cargar" con Reintentar (§6).
    const destino = destinoSegunHabilitacion(habilitacion);
    if (destino) return next(destino);
  }

  return next();
});

// Actualización dinámica de favicon y título (TFP en landings, Baquiano desde login en adelante)
function setFavicon(url) {
  if (typeof document === "undefined") return;
  // Remover links de favicon existentes para forzar al navegador a refrescar la pestaña
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach((link) => {
    if (link.parentNode) link.parentNode.removeChild(link);
  });

  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/png";
  link.sizes = "32x32";
  link.href = url;
  document.head.appendChild(link);

  const shortcut = document.createElement("link");
  shortcut.rel = "shortcut icon";
  shortcut.type = "image/png";
  shortcut.href = url;
  document.head.appendChild(shortcut);
}

router.afterEach((to) => {
  const landingRouteNames = [
    "landing",
    "vaquiano",
    "baquiano",
    "tfp",
    "tfpDev",
    "tfpAgro",
    "terms",
    "suscripciones",
  ];
  const isLanding =
    landingRouteNames.includes(to.name) ||
    to.path === "/landing" ||
    to.path === "/tfp" ||
    to.path === "/tfp-agro" ||
    to.path === "/vaquiano" ||
    to.path === "/baquiano";

  if (isLanding) {
    // 1. Landing pages: Vaquiano en /vaquiano (y /landing), TFP en el resto
    if (
      to.name === "vaquiano" ||
      to.name === "baquiano" ||
      to.name === "landing" ||
      to.path === "/vaquiano" ||
      to.path === "/baquiano" ||
      to.path === "/landing"
    ) {
      setFavicon("/favicon-vaquiano.png?v=2");
      document.title = "TFP Vaquiano - Software Agroganadero";
    } else {
      setFavicon("/favicon-tfp.png?v=white");
      if (
        to.name === "tfp" ||
        to.name === "tfpDev" ||
        to.path === "/tfp" ||
        to.path === "/tfp-dev"
      ) {
        document.title = "TFP Tecnología Argentina";
      } else if (to.name === "tfpAgro" || to.path === "/tfp-agro") {
        document.title = "TFP División Agro - Hardware y Software";
      } else {
        document.title = "TFP Tecnología Argentina";
      }
    }
  } else {
    // 2. Login en adelante (uso del software): Marca Vaquiano
    setFavicon("/favicon-vaquiano.png?v=2");
    const softwareTitles = {
      tambos: "Establecimientos · Vaquiano",
      login: "Iniciar Sesión · Vaquiano",
      register: "Crear Cuenta · Vaquiano",
      forgotPassword: "Recuperar Contraseña · Vaquiano",
      verify: "Verificación de Cuenta · Vaquiano",
      inicio: "Panel de Control · Vaquiano",
      vacas: "Gestión de Rodeo · Vaquiano",
      rodeoCria: "Rodeo de Cría · Vaquiano",
      feedlot: "Engorde y Feedlot · Vaquiano",
      despachos: "Despachos Lecheros · Vaquiano",
      facturacion: "Facturación ARCA · Vaquiano",
      dietas: "Dietas y Alimentación · Vaquiano",
      lotes: "Gestión de Lotes · Vaquiano",
      mapaAgr: "Mapas Agronómicos · Vaquiano",
      estadisticas: "Estadísticas · Vaquiano",
    };

    if (softwareTitles[to.name]) {
      document.title = softwareTitles[to.name];
    } else if (to.meta && to.meta.title) {
      document.title = `${to.meta.title} · Vaquiano`;
    } else {
      document.title = "Vaquiano · Software Ganadero";
    }
  }
});

export default router;
